import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  Mail, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  RefreshCw,
  TrendingUp,
  Calendar
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface InvitationAnalyticsProps {
  workspaceId: string;
}

interface AnalyticsData {
  totalInvitations: number;
  pendingInvitations: number;
  acceptedInvitations: number;
  expiredInvitations: number;
  conversionRate: number;
  recentBatches: Array<{
    id: string;
    batch_name: string;
    total_invitations: number;
    accepted_count: number;
    sent_at: string;
  }>;
}

export function InvitationAnalytics({ workspaceId }: InvitationAnalyticsProps) {
  const [timeframe, setTimeframe] = useState<'7d' | '30d' | '90d'>('30d');

  const { data: analytics, isLoading, refetch } = useQuery({
    queryKey: ['invitation-analytics', workspaceId, timeframe],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workspace-invitations', {
        body: {
          action: 'analytics',
          workspace_id: workspaceId,
          timeframe
        }
      });

      if (error) throw error;
      return data.data as AnalyticsData;
    },
    enabled: !!workspaceId
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Analytics...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-40 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  const conversionRate = analytics.totalInvitations > 0 
    ? (analytics.acceptedInvitations / analytics.totalInvitations) * 100 
    : 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Total Sent</p>
                <p className="text-2xl font-bold">{analytics.totalInvitations}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Accepted</p>
                <p className="text-2xl font-bold">{analytics.acceptedInvitations}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{analytics.pendingInvitations}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">Conversion Rate</p>
                <p className="text-2xl font-bold">{conversionRate.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Invitation Performance</CardTitle>
            <CardDescription>
              Track invitation success rates and engagement over time
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={timeframe} onValueChange={(value) => setTimeframe(value as any)}>
              <TabsList>
                <TabsTrigger value="7d">7 Days</TabsTrigger>
                <TabsTrigger value="30d">30 Days</TabsTrigger>
                <TabsTrigger value="90d">90 Days</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Conversion Rate Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Conversion Rate</span>
              <span>{conversionRate.toFixed(1)}%</span>
            </div>
            <Progress value={conversionRate} className="w-full" />
          </div>

          {/* Status Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{analytics.acceptedInvitations}</div>
              <div className="text-sm text-muted-foreground">Accepted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{analytics.pendingInvitations}</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{analytics.expiredInvitations}</div>
              <div className="text-sm text-muted-foreground">Expired</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {analytics.totalInvitations - analytics.acceptedInvitations - analytics.pendingInvitations - analytics.expiredInvitations}
              </div>
              <div className="text-sm text-muted-foreground">Declined</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Batches */}
      {analytics.recentBatches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Invitation Batches</CardTitle>
            <CardDescription>
              Performance of your latest bulk invitation campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.recentBatches.map((batch) => {
                const batchConversion = batch.total_invitations > 0 
                  ? (batch.accepted_count / batch.total_invitations) * 100 
                  : 0;

                return (
                  <div key={batch.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="font-medium">{batch.batch_name}</div>
                      <div className="text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 inline mr-1" />
                        {new Date(batch.sent_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">
                          {batch.accepted_count}/{batch.total_invitations} accepted
                        </Badge>
                        <Badge variant={batchConversion >= 50 ? 'default' : batchConversion >= 25 ? 'secondary' : 'destructive'}>
                          {batchConversion.toFixed(1)}%
                        </Badge>
                      </div>
                      <div className="w-32">
                        <Progress value={batchConversion} className="h-2" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}